import { IVertexGroup } from '@models/classes/kb3d-models';

export function removeVerticesFromAll(
    vertexGroups: Array<Required<IVertexGroup>>,
    startIndex: number,
    endIndex: number
): void {
    for (let i = 0; i < vertexGroups.length; i++) {
        const vertexGroup = vertexGroups[i];
        if (vertexGroupsIntersect(vertexGroup, startIndex, endIndex)) {
            const result = excludeVertices(vertexGroup, startIndex, endIndex);
            vertexGroups.splice(i, 1, ...result);
        }
    }
}

export function mergeVerticesIntoAll<T extends Required<IVertexGroup>>(vertexGroups: T[], newVertex: T): void {
    vertexGroups.push(newVertex);
    vertexGroups.sort((a, b) => a.startIndex! - b.startIndex!);

    for (let i = 0; i < vertexGroups.length; i++) {
        const vertexGroup = vertexGroups[i];
        const nextVertexGroup = vertexGroups[i + 1];

        if (nextVertexGroup && nextVertexGroup.startIndex < vertexGroup.endIndex) {
            const mergedVertexGroup = { ...vertexGroup, endIndex: nextVertexGroup.endIndex };
            vertexGroups.splice(i, 2, mergedVertexGroup);
            i--;
        }
    }
}

export function excludeVertices<T extends Required<IVertexGroup>>(
    vertexGroup: T,
    startIndex: number,
    endIndex: number
): T[] {
    if (vertexGroupsIntersect(vertexGroup, startIndex, endIndex)) {
        const result = [] as T[];

        // Maintain the selections of either side of the current vertex
        if (startIndex > vertexGroup.startIndex) {
            result.push({
                ...vertexGroup,
                endIndex: startIndex,
            });
        }
        if (endIndex < vertexGroup.endIndex) {
            result.push({
                ...vertexGroup,
                startIndex: endIndex,
            });
        }

        return result;
    } else {
        return [vertexGroup];
    }
}

export function mergeVertices<T extends Required<IVertexGroup>>(vertexGroup: T, newVertex: T): T[] {
    const result = [] as T[];
    const newVertexGroup = { ...vertexGroup };
    const startIndex = newVertex.startIndex;
    const endIndex = newVertex.endIndex;

    if (vertexGroupsIntersect(vertexGroup, startIndex, endIndex)) {
        // Maintain the selections of either side of the current vertex
        if (startIndex < vertexGroup.startIndex) {
            newVertexGroup.startIndex = startIndex;
        }
        if (endIndex > vertexGroup.endIndex) {
            newVertexGroup.endIndex = endIndex;
        }
        result.push(newVertexGroup);
    } else {
        if (startIndex > vertexGroup.startIndex) {
            result.push(newVertexGroup, newVertex);
        } else {
            result.push(newVertex, newVertexGroup);
        }
    }

    return result;
}

function vertexGroupsIntersect(vertexGroup: Required<IVertexGroup>, startIndex: number, endIndex: number) {
    return startIndex <= vertexGroup.endIndex && endIndex >= vertexGroup.startIndex;
}
